import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Edit, Save, Loader2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLegalPages = () => {
  const [pages, setPages] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    content: '',
    last_updated: '',
    enabled: true
  });

  useEffect(() => {
    fetchLegalPages();
  }, []);

  const fetchLegalPages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/localization/legal`);
      if (response.data.success) {
        setPages(response.data.pages);
      }
    } catch (err) {
      toast.error('Failed to fetch legal pages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = async (pageType) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/localization/legal/${pageType}`);
      if (response.data.success) {
        const page = response.data.page;
        setEditingPage(pageType);
        setEditForm({
          title: page.title || '',
          content: page.content || '',
          last_updated: page.last_updated || '',
          enabled: page.enabled !== false
        });
        setShowEditDialog(true);
      }
    } catch (err) {
      toast.error('Failed to fetch page content');
    }
  };

  const savePage = async () => {
    if (!editingPage) return;
    
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/localization/legal/${editingPage}`, editForm);
      
      // Update local state
      setPages(prev => ({
        ...prev,
        [editingPage]: {
          ...prev[editingPage],
          title: editForm.title,
          enabled: editForm.enabled,
          last_updated: editForm.last_updated,
          content_length: editForm.content.length
        }
      }));
      
      setShowEditDialog(false);
      toast.success('Legal page updated successfully');
    } catch (err) {
      toast.error('Failed to update legal page');
    } finally {
      setSaving(false);
    }
  };

  const togglePageEnabled = async (pageType, enabled) => {
    try {
      await axios.put(`${API_URL}/api/admin/localization/legal/${pageType}`, { enabled });
      
      setPages(prev => ({
        ...prev,
        [pageType]: {
          ...prev[pageType],
          enabled
        }
      }));
      
      toast.success(`${pages[pageType]?.title || pageType} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to update page status');
    }
  };

  const getPageTitle = (type) => {
    const titles = {
      privacy_policy: 'Privacy Policy',
      terms_of_service: 'Terms of Service'
    };
    return titles[type] || type;
  };

  const getPageUrl = (type) => {
    const urls = {
      privacy_policy: '/privacy',
      terms_of_service: '/terms'
    };
    return urls[type] || '#';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="h-6 w-6 text-blue-600" />
          Legal Pages Management
        </h1>
        <p className="text-gray-600 mt-1">
          Manage Privacy Policy, Terms of Service, and other legal pages
        </p>
      </div>

      <div className="grid gap-4">
        {Object.entries(pages).map(([pageType, page]) => (
          <Card key={pageType}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-gray-500" />
                  <div>
                    <CardTitle className="text-lg">{page.title || getPageTitle(pageType)}</CardTitle>
                    <CardDescription>
                      Last updated: {page.last_updated || 'Not set'} · {page.content_length?.toLocaleString() || 0} characters
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={page.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                    {page.enabled ? 'Published' : 'Draft'}
                  </Badge>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">
                      {page.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </span>
                    <Switch
                      checked={page.enabled}
                      onCheckedChange={(checked) => togglePageEnabled(pageType, checked)}
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center justify-between">
                <a 
                  href={getPageUrl(pageType)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  View page →
                </a>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditDialog(pageType)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Content
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Edit {getPageTitle(editingPage)}
            </DialogTitle>
            <DialogDescription>
              Update the content of this legal page. Use Markdown formatting for headings, lists, and emphasis.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Page Title</Label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Privacy Policy"
                />
              </div>
              <div>
                <Label>Last Updated Date</Label>
                <Input
                  value={editForm.last_updated}
                  onChange={(e) => setEditForm(prev => ({ ...prev, last_updated: e.target.value }))}
                  placeholder="12/03/2026"
                />
              </div>
            </div>
            
            <div>
              <Label>Content (Markdown supported)</Label>
              <Textarea
                value={editForm.content}
                onChange={(e) => setEditForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Enter page content here..."
                rows={20}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use # for headings, **bold**, *italic*, - for lists. {editForm.content.length.toLocaleString()} characters
              </p>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-sm">Publish Page</Label>
                <p className="text-xs text-gray-500">When disabled, the page will show a "not available" message</p>
              </div>
              <Switch
                checked={editForm.enabled}
                onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, enabled: checked }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={savePage} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLegalPages;
